import React, { useCallback, useContext } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Button } from '../../../../components/Button';
import { colors } from '../../../../global';
import { CoreContext } from '../../../../providers';
import { media } from '../../../../services/media';
import { themeStyles } from '../../../../services/themeStyles';

/**
 * @typedef {Object} Item
 */

/** @type {React.FC<{
 * item: Item,
 * onClick: any,
 * variant: "green"|"orange",
 * bankOffer: any
 * }>}
 */
export const BankOfferSlide = ({
    item,
    onClick,
    variant = 'green',
    bankOffer,
}: any) => {
    const { settings } = useContext(CoreContext);
    const buttonVariant = variant === 'orange' ? 'orange' : 'primary';

    const handleClick = useCallback(() => {
        onClick(item.info, item.offerID, bankOffer);
    }, [onClick, item.info, item.offerID, bankOffer]);

    if (!item || !buttonVariant) {
        return null;
    }

    const {
        heading,
        buttonLabelPositive,
        buttonLabelNegative,
        description,
        type,
    } = item;

    return (
        <Slide variant={variant} className="swiper-slide">
            {heading && (
                <Heading>
                    {heading} - {variant}
                </Heading>
            )}
            {description && (
                <Wrapper>
                    <Content>{description}</Content>
                    {bankOffer && (
                        <Button
                            variant={
                                settings.languageVersion === 'pengestyrke'
                                    ? 'boxed'
                                    : 'primary'
                            }
                            onClick={handleClick}
                            data-event={`popup trigger button (${item.info})`}
                        >
                            {type === 'positive'
                                ? buttonLabelPositive
                                : buttonLabelNegative}
                        </Button>
                    )}
                </Wrapper>
            )}
        </Slide>
    );
};

const SlideUpscore = themeStyles(
    'upscore',
    css`
        background-image: linear-gradient(to top, #15d8e2 0%, #54ef93 100%);
        border-radius: 30px;
    `,
);

const SlideUpscoreOrange = css`
    background-image: linear-gradient(to top, #e0565f 64%, #f2af64 100%);
`;

const SlidePenge = themeStyles(
    'pengestyrke',
    css`
        background-color: #9ccb5b;
    `,
);

const SlidePengeOrange = css`
    background-color: #eb5151;
`;

const Slide = styled.div<{ variant: 'green' | 'orange' }>`
    padding: 3px;
    display: flex;
    justify-content: space-between;
    flex-direction: column;

    ${SlideUpscore}
    ${SlidePenge}

    ${(props) => props.variant === 'orange' && SlideUpscoreOrange}
    ${(props) => props.variant === 'orange' && SlidePengeOrange}
`;

const Heading = styled.div`
    color: white;
    text-align: center;
    font-weight: 400;
    line-height: 18px;
    padding-top: 9px;
    padding-bottom: 12px;
    font-family: $font-family-1;

    ${media.min('md')} {
        line-height: 21px;
        padding-top: 14px;
        padding-bottom: 17px;
    }

    ${media.min('xl')} {
        line-height: 25px;
    }
`;

const UpscoreWrapper = themeStyles(
    'upscore',
    css`
        border-radius: 0 0 27px 27px;
    `,
);

const Wrapper = styled.div`
    background: ${colors.bonJour};
    padding: 30px;
    text-align: center;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    ${UpscoreWrapper}

    ${media.min('xl')} {
        padding: 50px;
    }
`;

const Content = styled.div`
    font-weight: 300;
    line-height: 20px;
    margin-bottom: 30px;

    ${media.min('xl')} {
        line-height: 28px;
        max-width: 70%;
        margin-left: auto;
        margin-right: auto;
        margin-bottom: 50px;
    }
`;
